/**
 * MSAL configuration for the BOLT frontend SPA.
 *
 * Two App Registrations are used:
 *   BOLT-DEV-Frontend-SPA  — public client (no secret), PKCE, drives the
 *                            browser-side OAuth flow.  Client ID is set via
 *                            VITE_AZURE_CLIENT_ID.
 *   BOLT-DEV-Backend-API   — API resource that the backend validates against.
 *                            Its App ID URI is set via VITE_AZURE_BACKEND_APP_ID
 *                            (e.g. "api://bolt-dev-backend").
 *
 * MSAL (v5) caches tokens in sessionStorage so that each browser tab is
 * isolated and tokens are cleared when the tab is closed.
 */

import { Configuration, RedirectRequest, PublicClientApplication } from "@azure/msal-browser";

export const msalConfig: Configuration = {
  auth: {
    /**
     * Client ID of the Frontend SPA App Registration (BOLT-DEV-Frontend-SPA).
     */
    clientId: (import.meta.env as Record<string, string>)["VITE_AZURE_CLIENT_ID"] ?? "",
    /**
     * Tenant-specific authority — only accounts in our tenant can authenticate.
     */
    authority: `https://login.microsoftonline.com/${(import.meta.env as Record<string, string>)["VITE_AZURE_TENANT_ID"] ?? "common"}`,
    /**
     * MSAL redirectUri defaults to window.location.origin so the browser
     * stays on whichever hostname the user entered (custom domain or AFD
     * endpoint). No build-time VITE_REDIRECT_URI is needed.
     */
    redirectUri: (typeof window !== "undefined" ? window.location.origin : ""),
  },
  cache: {
    /**
     * sessionStorage: tokens are scoped to the tab and cleared on close.
     */
    cacheLocation: "sessionStorage",
  },
  system: {
    loggerOptions: {
      loggerCallback: (_level, message, containsPii) => {
        if (!containsPii && import.meta.env.DEV) {
          console.debug(`[MSAL] ${message}`);
        }
      },
    },
  },
};

/**
 * Scopes requested at login.
 *
 * Requesting user_impersonation on the Backend App Registration causes Azure AD
 * to issue an access token whose audience is VITE_AZURE_BACKEND_APP_ID.
 * The backend validates that audience on every API request.
 */
export const backendApiRequest: RedirectRequest = {
  scopes: [
    `${(import.meta.env as Record<string, string>)["VITE_AZURE_BACKEND_APP_ID"] ?? "api://bolt-backend"}/access_as_user`,
  ],
};

/**
 * Singleton MSAL instance — shared across the whole application.
 * Exported so that api.ts can call acquireTokenSilent outside of React
 * components (where useMsal is not available).
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// NOTE: initialize() is intentionally NOT called here.
// index.tsx awaits it before calling ReactDOM.render() so that
// handleRedirectPromise completes (and cleans up the URL) before React mounts.
